import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import GridBackground from "@/components/primitives/GridBackground";

const STATS = [
    { value: "∞", label: "Continuous operation",  type: "infinity" },
    { value: 0,   label: "Hard cutoffs",           type: "number" },
    { value: "Adaptive", label: "Throughput allocation", type: "word" },
];

function CountUp({ to, durationMs = 1200 }) {
    const [val, setVal] = useState(0);
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-50px" });

    useEffect(() => {
        if (!inView) return;
        const start = performance.now();
        let frame;
        const tick = (now) => {
            const t = Math.min(1, (now - start) / durationMs);
            const eased = 1 - Math.pow(1 - t, 3);
            setVal(Math.round(eased * to));
            if (t < 1) frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [inView, to, durationMs]);

    return <span ref={ref}>{val}</span>;
}

/**
 * Hexagonal flowing-bg via SVG with CSS animations on stroke-dashoffset.
 */
function HexNetwork() {
    // 6×4 grid of hexes
    const cols = 8;
    const rows = 5;
    const hexW = 120;
    const hexH = 104;
    const cells = [];
    for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
            const x = c * hexW + (r % 2 ? hexW / 2 : 0);
            const y = r * hexH * 0.78;
            cells.push({ x, y, key: `${r}-${c}` });
        }
    }
    return (
        <svg
            viewBox={`0 0 ${cols * hexW + hexW / 2} ${rows * hexH * 0.78 + hexH}`}
            preserveAspectRatio="xMidYMid slice"
            className="w-full h-full"
            aria-hidden="true"
        >
            <defs>
                <linearGradient id="hexFlow" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="rgba(0,224,232,0)" />
                    <stop offset="50%" stopColor="rgba(0,224,232,0.5)" />
                    <stop offset="100%" stopColor="rgba(0,224,232,0)" />
                </linearGradient>
            </defs>
            <g fill="none" stroke="rgba(0,224,232,0.18)" strokeWidth="0.7">
                {cells.map(({ x, y, key }, i) => {
                    const cx = x + hexW / 2;
                    const cy = y + hexH / 2;
                    const r = hexW / 2 - 4;
                    const points = [];
                    for (let i = 0; i < 6; i++) {
                        const a = (Math.PI / 3) * i + Math.PI / 6;
                        points.push(`${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`);
                    }
                    const pulseDelay = ((i * 0.32) % 6).toFixed(2);
                    return (
                        <polygon
                            key={key}
                            points={points.join(" ")}
                            stroke={i % 7 === 3 ? "rgba(0,224,232,0.45)" : "rgba(0,224,232,0.16)"}
                        >
                            <animate
                                attributeName="stroke-opacity"
                                values="0.12;0.45;0.12"
                                dur="6s"
                                begin={`${pulseDelay}s`}
                                repeatCount="indefinite"
                            />
                        </polygon>
                    );
                })}
            </g>
        </svg>
    );
}

export default function QBandSection() {
    return (
        <section
            id="qband"
            data-testid="qband-section"
            className="relative bg-tca-panel overflow-hidden"
        >
            {/* Hex network */}
            <div
                aria-hidden="true"
                className="absolute inset-0 opacity-65 pointer-events-none"
                style={{
                    WebkitMaskImage:
                        "radial-gradient(ellipse 90% 70% at 50% 50%, #000 30%, transparent 100%)",
                    maskImage:
                        "radial-gradient(ellipse 90% 70% at 50% 50%, #000 30%, transparent 100%)",
                }}
            >
                <HexNetwork />
            </div>
            <GridBackground fine vignette={false} />

            <div className="relative z-10 max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 py-28 sm:py-40 text-center">
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.6 }}
                    className="font-mono-tight text-[10px] sm:text-[11px] uppercase tracking-[0.36em] text-tca-cyan/85 mb-6"
                    data-testid="qband-eyebrow"
                >
                    // Continuity Doctrine
                </motion.div>

                <motion.h2
                    initial={{ opacity: 0, y: 18, filter: "blur(8px)" }}
                    whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 1.0, ease: [0.22, 1, 0.36, 1] }}
                    data-testid="qband-headline"
                    className="font-display text-[48px] leading-[1.0] sm:text-[80px] sm:leading-[0.96] lg:text-[112px] lg:leading-[0.94] tracking-[-0.03em] text-tca-ink mx-auto max-w-5xl"
                >
                    We <span className="italic neon-cyan font-light">removed</span> tokens.
                </motion.h2>

                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="font-display italic text-tca-ink-muted text-xl sm:text-2xl mt-6"
                >
                    Your system does not stop. It adapts.
                </motion.div>

                {/* 3 stat blocks */}
                <div
                    data-testid="qband-stats"
                    className="
                        mt-16 sm:mt-20 grid grid-cols-1 sm:grid-cols-3
                        gap-6 sm:gap-8 max-w-4xl mx-auto
                    "
                >
                    {STATS.map((s, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 0.7, delay: 0.1 * i, ease: [0.22, 1, 0.36, 1] }}
                            className="relative"
                            data-testid={`qband-stat-${i}`}
                        >
                            <div className="border-t border-tca-cyan/30 pt-6 sm:pt-8">
                                <div
                                    className="font-display text-tca-cyan-hi text-5xl sm:text-6xl lg:text-7xl leading-none"
                                    style={{
                                        textShadow: "0 0 18px rgba(0,224,232,0.55)",
                                        animation: s.type === "infinity" ? "tca-pulse-soft 2.6s ease-in-out infinite" : undefined,
                                    }}
                                >
                                    {s.type === "number" ? <CountUp to={s.value} /> : s.value}
                                </div>
                                <div className="mt-4 font-mono-tight text-[11px] sm:text-xs uppercase tracking-[0.28em] text-tca-ink-muted">
                                    {s.label}
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Footer body */}
                <motion.p
                    initial={{ opacity: 0, y: 8 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                    className="mt-16 sm:mt-20 max-w-2xl mx-auto text-tca-ink-muted text-base sm:text-lg leading-relaxed"
                >
                    Q-Band replaces token systems with continuous bandwidth allocation.
                    Your work does not pause when limits hit. The ecosystem adapts.
                </motion.p>
            </div>

            <style>{`
                @keyframes tca-pulse-soft {
                    0%, 100% { opacity: 0.85; transform: scale(1); }
                    50% { opacity: 1; transform: scale(1.04); }
                }
            `}</style>
        </section>
    );
}
